import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import { useAuthenticatedUser } from 'c-hooks';
import { useEntityData } from 'c-data';
import { describePage, onTrack } from 'c-lib';
import { useCommonTranslation } from 'c-translation';
import { uiSelectors } from 'c-wrapper/Slices/UI';
import { UserType } from 'c-sdk';
import { allRouteTemplates, areaFor } from 'c-admin/Pages/Usage/usageAreas';
import type { BaseEntity } from 'c-sdk';
import type { User } from 'c-entity-types';

/**
 * Microsoft Clarity session replays and heatmaps, for signed-in users only. Agreed with Ben
 * (Oct 2026): send who the user is, by name, so recordings are findable.
 *
 * - `identify` with our user id and a label of name and organisation. Clarity hashes the id it
 *   shows, so `user_id` also goes as a tag, unhashed: that is what to filter on.
 * - Session tags: role, organisation, skin, staff (any `admin` or `admin.*` permission).
 * - Page tags on every route change: `page` (route template), `area`, and `campaign_id` on a
 *   campaign page. Clarity keeps every value a session had.
 * - Every `track()` action (c-lib `activity.ts`) also goes to Clarity as an event.
 * - Analytics consent is granted on start (no banner; ads denied). Without it Clarity treats UK
 *   visitors as no-consent: a new user per page and no custom id.
 * - Never started for an admin impersonating someone. Clarity can't be stopped once running, so a
 *   session that starts impersonating later is tagged `impersonating`.
 */

type Named = BaseEntity & { name?: string };

const ROUTES = allRouteTemplates();

const organisationFor = (user: User, type?: UserType): number | undefined => {
    switch (type) {
        case UserType.Agency:
            return user.agency_id ?? user.agencies?.[0];
        case UserType.Buyer:
            return user.buyer_id ?? user.buyers?.[0];
        case UserType.Client:
            return user.client_id ?? user.clients?.[0];
        case UserType.Owner:
            return user.owner_id ?? user.owners?.[0];
        default:
            return undefined;
    }
};

/** The organisation's name if its entity is already in the store; nothing is fetched for it. */
const useOrganisationName = (type: UserType | undefined, id: number | undefined) => {
    const agencies = useEntityData<Named>('Agency');
    const buyers = useEntityData<Named>('Buyer');
    const clients = useEntityData<Named>('Client');
    const owners = useEntityData<Named>('Owner');

    if (id == null) return undefined;
    switch (type) {
        case UserType.Agency:
            return agencies.getById({ id })?.name;
        case UserType.Buyer:
            return buyers.getById({ id })?.name;
        case UserType.Client:
            return clients.getById({ id })?.name;
        case UserType.Owner:
            return owners.getById({ id })?.name;
        default:
            return undefined;
    }
};

function ClarityInitialiser(): null {
    const t = useCommonTranslation();
    const { user, chosenUserType } = useAuthenticatedUser();
    const skin = useSelector(uiSelectors.previewThemeRaw);
    const { pathname } = useLocation();
    const started = useRef(false);
    const impersonating = user?.impersonatedBy != null;
    const organisationId = user ? organisationFor(user, chosenUserType) : undefined;
    const organisationName = useOrganisationName(chosenUserType, organisationId);
    const staff = useMemo(
        () =>
            (user?.permissionNames ?? []).some(
                name => name === 'admin' || name.startsWith('admin.'),
            ),
        [user],
    );

    useEffect(() => {
        if (!user) return;

        if (impersonating) {
            if (started.current) Clarity.setTag('impersonating', 'true');
            return;
        }

        if (!started.current) {
            Clarity.init(String(import.meta.env.VITE_CLARITY_PROJECT_ID));
            // Clarity needs a consent signal for UK/EEA visitors (since Oct 2025). Without one it
            // runs in no-consent mode: a new random user per page view, so `identify` never sticks.
            // Analytics is granted on sign-in with no banner (Ben's call, Oct 2026); ads never.
            Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'granted' });
            started.current = true;
        }

        const role = chosenUserType ? t(`Modules.Admin.Usage.types.${chosenUserType}`) : undefined;
        const label = [user.name, organisationName ?? role].filter(Boolean).join(' · ');
        Clarity.identify(String(user.id), undefined, undefined, label || undefined);
        Clarity.setTag('user_id', String(user.id));
        Clarity.setTag('staff', staff ? 'yes' : 'no');
        if (chosenUserType) Clarity.setTag('role', chosenUserType);
        if (organisationId != null) Clarity.setTag('organisation_id', String(organisationId));
        if (organisationName) Clarity.setTag('organisation', organisationName);
        if (skin?.id != null) Clarity.setTag('skin', String(skin.id));
    }, [user, impersonating, chosenUserType, organisationId, organisationName, staff, skin?.id, t]);

    useEffect(() => {
        if (!started.current || impersonating) return;
        const page = describePage(pathname, ROUTES);
        Clarity.setTag('page', page.name);
        Clarity.setTag('area', areaFor(page.name));
        if (page.subject_type === 'campaign' && page.subject_id) {
            Clarity.setTag('campaign_id', page.subject_id);
        }
    }, [pathname, impersonating, user]);

    useEffect(() => {
        if (!user || impersonating) return undefined;
        return onTrack(name => {
            if (started.current) Clarity.event(name);
        });
    }, [user, impersonating]);

    return null;
}

export default ClarityInitialiser;
